/**
 * useGoogleCalendarEvents Hook
 *
 * Loads the Google Calendar a location connected so the calendar can show it
 * next to Chekkit events and reminders. With AI Employee that is the booking
 * calendar; otherwise it is the connected account's primary calendar.
 *
 * Google events are read-only. Calendars the owner only shared for busy
 * times come back as "Busy" blocks with no details.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import {
	CalendarEventType,
	CalendarViewType,
} from '@/components/event-calendar/types';
import { aiGet } from '@/components/ai-employee/hooks/use-ai-api';
import { pushJobsToGoogleCalendar } from '@/utils/googleCalendarJobSync';
import {
	getVisibleRange,
	GoogleCalendarEventDTO,
	GoogleCalendarSource,
	toCalendarEvent,
} from '@/utils/googleCalendarEvents';

export type { GoogleCalendarSource };

interface GoogleCalendarEventsResponse {
	status: 'ready' | 'disconnected' | 'reconnect' | 'unconfigured';
	calendars: GoogleCalendarSource[];
	events: GoogleCalendarEventDTO[];
}

export type GoogleCalendarStatus =
	| 'off' // No location is signed in yet
	| 'loading'
	| 'error'
	| GoogleCalendarEventsResponse['status'];

interface UseGoogleCalendarEventsProps {
	currentDate: Date;
	currentView: CalendarViewType;
	// Push changed jobs to Google; only the calendar page should do this
	syncJobs?: boolean;
}

interface UseGoogleCalendarEventsReturn {
	status: GoogleCalendarStatus;
	calendars: GoogleCalendarSource[];
	events: CalendarEventType[];
	reload: () => void;
}

const REFRESH_ON_FOCUS_AFTER_MS = 60 * 1000;

export const useGoogleCalendarEvents = ({
	currentDate,
	currentView,
	syncJobs = true,
}: UseGoogleCalendarEventsProps): UseGoogleCalendarEventsReturn => {
	const auth = useSelector(
		(state: { auth?: { _id?: string; products?: string[] } }) => state.auth
	);
	const businessId = auth?._id;
	const products = auth?.products;
	const enabled = Boolean(businessId);

	const [status, setStatus] = useState<GoogleCalendarStatus>(
		enabled ? 'loading' : 'off'
	);
	const [calendars, setCalendars] = useState<GoogleCalendarSource[]>([]);
	const [items, setItems] = useState<GoogleCalendarEventDTO[]>([]);
	const latestRequest = useRef(0);
	const loadedAt = useRef(0);

	const { start, end } = getVisibleRange(currentDate, currentView);
	const startISO = start.toISOString();
	const endISO = end.toISOString();

	const load = useCallback(async () => {
		if (!enabled) {
			setStatus('off');
			return;
		}

		const request = ++latestRequest.current;
		loadedAt.current = Date.now();

		try {
			const query = new URLSearchParams({ start: startISO, end: endISO });
			const data = await aiGet<GoogleCalendarEventsResponse>(
				`/api/ai-employee/calendar/${businessId}/events?${query}`
			);
			// A newer range was requested while this one was in flight
			if (request !== latestRequest.current) return;

			setStatus(data.status);
			setCalendars(data.calendars);
			setItems(data.events);
		} catch (error) {
			if (request !== latestRequest.current) return;
			// Keep what's already on screen; a failed refresh shouldn't blank it
			console.error('Failed to load Google Calendar events:', error);
			setStatus('error');
		}
	}, [enabled, businessId, startISO, endISO]);

	useEffect(() => {
		load();
	}, [load]);

	// Jobs changed somewhere else, such as the mobile app, reach Google when
	// the calendar opens. Does nothing unless the location turned that on.
	useEffect(() => {
		if (enabled && syncJobs)
			pushJobsToGoogleCalendar({ _id: businessId, products });
	}, [enabled, syncJobs, businessId, products]);

	// Pick up appointments booked while the tab was in the background
	useEffect(() => {
		const handleFocus = () => {
			if (Date.now() - loadedAt.current > REFRESH_ON_FOCUS_AFTER_MS)
				load();
		};

		window.addEventListener('focus', handleFocus);
		return () => window.removeEventListener('focus', handleFocus);
	}, [load]);

	const events = useMemo(() => {
		const byId = new Map(
			calendars.map((calendar) => [calendar.id, calendar])
		);

		return items.flatMap((item) => {
			const calendar = byId.get(item.calendarId);
			return calendar ? [toCalendarEvent(item, calendar)] : [];
		});
	}, [calendars, items]);

	return { status, calendars, events, reload: load };
};
